/**
 * Sentry — external error monitoring.
 *
 * Uses @sentry/capacitor as the primary SDK so that BOTH JavaScript errors
 * (React layer) AND native iOS/Android crashes (Capacitor bridge) are
 * captured in a single init call. @sentry/react is passed as the sibling
 * init function — this is the documented pattern that enables native capture
 * without losing browser-side integrations (tracing, replay, breadcrumbs).
 *
 * Wraps the existing in-house errorTracking.ts (custom error queue → POST
 * to /api/client-errors). Both systems run in parallel:
 *  - errorTracking  → audit trail in our DB (cheap, always present)
 *  - Sentry          → observability layer (grouping, alerts, source maps,
 *                       native crash capture, PostHog session replay link)
 *
 * Zero-config when `VITE_SENTRY_DSN` is unset: `initSentry()` returns
 * immediately, no SDK side effects, no network traffic.
 *
 * PostHog ↔ Sentry link:
 *  lib/posthog.ts calls `setSessionUrlProvider()` after posthog.init() so
 *  our `beforeSend` hook can tag every Sentry event with the matching
 *  PostHog session replay URL. No circular import — posthog.ts depends on
 *  sentry.ts, not the other way round. `sentry.ts` never imports posthog.
 *
 * Secrets:
 *  VITE_SENTRY_DSN         — frontend DSN (public; safe to commit)
 *  VITE_RELEASE_VERSION    — git SHA injected at build time; must match
 *                             the backend's RELEASE_VERSION so errors
 *                             correlate across the stack (task 01)
 *  SENTRY_AUTH_TOKEN       — BUILD-TIME only; used by @sentry/vite-plugin
 *                             to upload source maps. NEVER bundled into the
 *                             client output. Set in Codemagic secrets only.
 */
import { safeUrl } from "./safeUrl";
import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { optionalImport } from "@/lib/optionalChunk";

let initialized = false;

/**
 * PostHog session-replay URL provider.
 * Called by lib/posthog.ts after posthog.init() so the beforeSend hook
 * can attach the session replay link to every Sentry event synchronously.
 */
let _sessionUrlProvider: (() => string | null) | null = null;

export function setSessionUrlProvider(fn: () => string | null): void {
  _sessionUrlProvider = fn;
}

export function initSentry(): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return; // no-op in dev / un-provisioned environments

  try {
    // @sentry/capacitor v4's CapacitorOptions type does not yet expose
    // replaysSessionSampleRate / replaysOnErrorSampleRate — those properties
    // live on BrowserOptions which the SentryReact.init sibling picks up at
    // runtime. We cast to `any` here rather than fight the type mismatch
    // between CapacitorOptions and BrowserOptions across SDK versions.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Sentry.init(<any>{
      dsn,

      // 'development' | 'production' from Vite's MODE — gives the
      // dev/prod split for free without extra config.
      environment: (import.meta.env.MODE as string) || "production",

      // VITE_RELEASE_VERSION is the git SHA injected at build time
      // (see vite.config.ts sentryVitePlugin + Fastfile).
      // Must match the backend's RELEASE_VERSION (task 01) so frontend
      // and backend errors share a release in the Sentry UI.
      // Falls back to the semver VITE_APP_VERSION if the git SHA isn't
      // available (local dev without the build script).
      release:
        (import.meta.env.VITE_RELEASE_VERSION as string | undefined) ||
        (import.meta.env.VITE_APP_VERSION as string | undefined) ||
        undefined,

      tracesSampleRate: 0.1,

      integrations: [
        // Browser performance tracing (fetch/xhr/navigation spans)
        SentryReact.browserTracingIntegration(),
        // Session replay is NOT listed here — it is added lazily after the
        // page has loaded (addReplayLater below). It's the single biggest
        // part of the Sentry SDK, and listing it here put ~125 KB of
        // minified code into the main chunk.
      ],

      // Never record random sessions (cost + privacy).
      // Always record the window around an error so we can see "what
      // happened before the crash" — the main replay value.
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,

      // Don't send user emails / names to Sentry.
      sendDefaultPii: false,

      // Drop browser-extension noise we don't control.
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured",
        "Network request failed", // user offline; our queue handles this
        // The browser's redacted cross-origin error — another site's script
        // threw and we're told nothing else. Never our bundle (2026-10-07).
        /^Script error\.?$/,
        // @sentry/capacitor's OWN internal error: its native-bridge
        // methods (sendEnvelope etc.) throw Error('Native is disabled')
        // when enableNative is false — which is always true on the web
        // build running in a desktop browser (no native iOS layer). It's
        // the monitoring SDK's plumbing noticing native isn't available,
        // not an app bug. Source: @sentry/capacitor/dist/esm/wrapper.js
        // line 386 (_DisabledNativeError). Founder report 2026-06-13,
        // issue from desktop Chrome/Windows at trybeup.com.
        "Native is disabled",
        // Benign Web Locks AbortError from the GoTrue auth client's
        // cross-tab token coordination — the lock-steal is part of the
        // protocol, not a failure (the operation retries). Confirmed
        // 2026-06-13 as 100% client-side (0 server events) and
        // non-actionable. Also filtered from the /api/client-errors
        // pipeline in lib/errorTracking.ts so it stops the founder's
        // crash-alert emails + Errors-tab noise.
        // Two phrasings because @supabase/auth-js changed the wording
        // mid-2026: older versions said "Lock was stolen…", current
        // versions say "Lock broken by another request with the
        // 'steal' option." (founder report 2026-06-26, issue dc18c12d
        // + 65d2c193).
        "Lock was stolen by another request",
        "Lock broken by another request with the 'steal' option",
        // Dynamic-import / lazy-chunk load failures — self-healed by the
        // one-reload-per-minute handler in main.tsx (fetches a fresh bundle),
        // so the user recovers automatically. Non-actionable noise otherwise.
        // Kept in sync with lib/errorTracking.ts (crash-alert pipeline) and
        // main.tsx's CHUNK_ERROR_RE. (crash e6ae6367, build 1.1.626 · ios.)
        "Importing a module script failed",
        "Failed to fetch dynamically imported module",
        "error loading dynamically imported module",
      ],

      /**
       * PostHog ↔ Sentry link.
       *
       * Attaches the PostHog session replay URL to every Sentry event
       * as a custom context entry. This makes a Sentry issue link
       * directly to the matching PostHog session replay so you can
       * watch exactly what the user did before the crash.
       *
       * The provider is set by lib/posthog.ts once posthog.init()
       * resolves. Events captured during the tiny posthog-init window
       * will be missing the link — acceptable tradeoff.
       */
      // Performance transactions carry the page address too (HttpContext) and
      // skip beforeSend — same cleaning (review 2026-10-08).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      beforeSendTransaction(event: any) {
        try {
          if (event?.request?.url) event.request.url = safeUrl(event.request.url);
        } catch { /* never block a report over this */ }
        return event;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      beforeSend(event: any, hint: any) {
        // Sign-in keys ride in the address (/#access_token=…) for a moment
        // after Google / email-link sign-in — never send them (lib/safeUrl.ts).
        try {
          if (event?.request?.url) event.request.url = safeUrl(event.request.url);
          for (const b of event?.breadcrumbs ?? []) {
            if (b?.data?.from) b.data.from = safeUrl(b.data.from);
            if (b?.data?.to) b.data.to = safeUrl(b.data.to);
            if (b?.data?.url) b.data.url = safeUrl(b.data.url);
          }
        } catch { /* never block a report over this */ }
        try {
          // ── Axios error attribution ──────────────────────────────────
          // Minified axios rejections (e.g. AxiosError: status 403) all
          // share an identical stack — `Nr` + `XMLHttpRequest.O` inside
          // vendor-query — so Sentry collapses EVERY axios failure across
          // EVERY endpoint into one unactionable mega-issue with no URL
          // (founder report 2026-06-13, issue 7547208048). Pull the
          // request context off the original exception and (a) drop the
          // expected/benign consent-gate 403, (b) tag the rest with their
          // URL/method/status, (c) fingerprint by endpoint so a 403 on
          // /coach/nudges and a 403 on /trybes become DISTINCT issues.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const err: any = hint?.originalException;
          const cfg = err?.config;
          const status = err?.response?.status;
          if (err?.name === "AxiosError" && cfg) {
            const rawUrl: string = cfg.url || "";
            // Normalise: strip query string + leading origin so the
            // fingerprint groups by route, not by per-request params.
            const path = rawUrl.replace(/^https?:\/\/[^/]+/, "").split("?")[0];
            const method = (cfg.method || "get").toUpperCase();
            const detail = err?.response?.data?.detail;

            // ── 403 triage: two tiers ────────────────────────────
            // Tier 1 (DROP): detail strings that uniquely identify an
            // intentional business-logic gate — only the matching
            // backend raise can produce these, so the event is pure
            // noise and we can drop it entirely.
            const DROPPABLE_403_DETAILS = new Set<string>([
              "ai_consent_required",                       // coach.py — pre-consent AI hit
              "Not a participant in this conversation",    // messages.py
            ]);
            if (status === 403 && typeof detail === "string" && DROPPABLE_403_DETAILS.has(detail)) {
              return null;
            }
            // Profile gate (2026-08-04): an incomplete account gets this 403
            // from EVERY non-exempt endpoint while the blocking card is on
            // screen — ~50 queries per app load, each with a different path,
            // each minting its own Sentry issue. The state is intentional
            // and self-resolving (the user fills the card), so drop it.
            // Note: detail is an OBJECT here, so the string tiers below
            // never match it.
            if (status === 403 && typeof detail === "object" && detail?.code === "profile_incomplete") {
              return null;
            }
            // Same tier 1 treatment for free-tier quota prompts (the
            // count is dynamic so we match by prefix).
            if (status === 403 && typeof detail === "string" && (
              detail.startsWith("Free tier allows a maximum of") ||
              detail.startsWith("Adding these habits would exceed")
            )) {
              return null;
            }

            // Tier 2 (KEEP + GROUP): generic detail strings like
            // "Access denied" / "Not authorised" are used by many
            // ownership checks. Dropping them silently would hide a
            // future bug that happens to throw the same string.
            // Instead, fingerprint by (detail, method, path) so each
            // route's known-benign 403s group together (easy to
            // dismiss en bloc) AND a brand-new route suddenly
            // emitting them shows up as a fresh issue you can
            // investigate. No data loss, just better grouping.
            const GENERIC_403_DETAILS = new Set<string>([
              "Not authorised",                            // coach.py + journal.py + notifications.py — non-owner
              "Access denied",                             // workouts.py — non-owner
            ]);
            const isGeneric403 = status === 403 && typeof detail === "string" && GENERIC_403_DETAILS.has(detail);

            event.tags = {
              ...event.tags,
              "api.path": path,
              "api.method": method,
              "api.status": String(status ?? "none"),
            };
            if (isGeneric403) {
              // One issue per (detail, method, path) so route-level
              // patterns are visible. e.g. all GET /coach/messages
              // "Not authorised" 403s group separately from POST
              // /journal "Not authorised" ones.
              event.fingerprint = ["axios-generic-403", String(detail), method, path];
            } else {
              // Split the mega-issue: one Sentry issue per (method,
              // path, status) instead of one for all axios failures
              // everywhere.
              event.fingerprint = ["axios", method, path, String(status ?? "none")];
            }
          }

          const url = _sessionUrlProvider?.();
          if (url) {
            event.contexts = {
              ...event.contexts,
              posthog: { "session replay url": url },
            };
          }
        } catch {
          /* never block error reporting */
        }
        return event;
      },
    },

    // ── Sibling init ──────────────────────────────────────────────────────
    // Passing SentryReact.init as the second argument is what enables
    // native crash capture for Capacitor. The @sentry/capacitor SDK hooks
    // the native layer; @sentry/react handles browser-side integrations.
    // Without this second argument, native-layer iOS crashes (e.g. plugin
    // failures, Capacitor bridge crashes) would be a complete blind spot
    // — they don't fire window.onerror / unhandledrejection.
    SentryReact.init);

    initialized = true;
    addReplayLater();
  } catch {
    // Defensive: a Sentry init failure must never crash app boot.
  }
}

/**
 * Session replay, loaded after first paint (2026-10-08).
 *
 * Replay records a video of the user's session ONLY when an error occurs
 * (replaysOnErrorSampleRate above — no continuous recording, quota +
 * privacy). Error CAPTURE is unaffected: it's live from the init call
 * above. The only thing given up is the replay buffer for an error thrown
 * in the first second or two, before this chunk has arrived.
 *
 * It's imported from @sentry-internal/replay — the package @sentry/react
 * re-exports replayIntegration from, pinned to the same exact version by
 * @sentry/browser — because a dynamic import of '@sentry/react' itself
 * would resolve to the main chunk (it's already statically imported) and
 * save nothing. The chunk ships inside the Capgo bundle like every other
 * lazy chunk, so the native app loads it from disk, never from a CDN.
 * addIntegration reads the replay sample rates from the options above.
 */
function addReplayLater(): void {
  const load = () => {
    optionalImport(() => import("@sentry-internal/replay"))
      .then(({ replayIntegration }) => {
        SentryReact.addIntegration(replayIntegration());
      })
      .catch(() => {
        /* replay is a nice-to-have; errors still report without it */
      });
  };
  const whenIdle = () => {
    const ric = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (ric) ric(load, { timeout: 5000 });
    else setTimeout(load, 1500); // iOS WebKit has no requestIdleCallback
  };
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
}

/** Manually report an error with optional context. Cheaper than `throw`
 *  for non-fatal anomalies you still want to see in Sentry. */
export function reportError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  try {
    if (err instanceof Error) {
      Sentry.captureException(err, context ? { extra: context } : undefined);
    } else {
      Sentry.captureMessage(String(err), { level: "error", extra: context });
    }
  } catch {
    /* never let monitoring crash the app */
  }
}

/**
 * Set the current user context on Sentry events.
 * Call on sign-in (id only — no PII). Call with null on sign-out.
 * Matches the backend's `sentry_sdk.set_user({"id": str(user.id)})` so
 * an error can be traced end-to-end across the full stack.
 */
export function setSentryUser(userId: string | null): void {
  if (!initialized) return;
  try {
    if (userId) {
      Sentry.setUser({ id: userId }); // id only — no PII
    } else {
      Sentry.setUser(null);
    }
  } catch {
    /* ignore */
  }
}
